import React, { useMemo } from 'react';
import { IconButton } from '../../../controls/Controls.jsx';
import {
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FileSearchOutlined,
  StopOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ROLE_FRI_OPEN_ADMIN,
  ROLE_FRI_OPEN_PARTICIPANT,
} from '../../../../../utils/constants/roles.js';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';

export function DatesListTableSettings({
  date,
  onEditClick,
  onDetailClick,
  onDelete,
  onCopyClick,
  isMyDates,
  onSignOnClick,
  onUnsignOnClick,
  onParticipantsClick,
  onToggleEnabled,
  isArchivedParent,
}) {
  const { t } = useTranslation();
  const user = useSelector((s) => s.auth?.user);

  const isAdmin = useUserHasRole(ROLE_FRI_OPEN_ADMIN);
  const isFriOpenParticipant = useUserHasRole(ROLE_FRI_OPEN_PARTICIPANT);

  const isAuthor = useMemo(() => {
    return user?.id && date?.creator_id
      ? Number(user.id) === Number(date.creator_id)
      : false;
  }, [user?.id, date?.creator_id]);

  const isLecturer = useMemo(() => {
    return user?.id && date?.lecturer_id
      ? Number(user.id) === Number(date.lecturer_id)
      : false;
  }, [user?.id, date?.lecturer_id]);

  const isUserRegistered = useMemo(() => {
    if (isMyDates) {
      return true;
    }

    if (typeof date?.is_user_registered === 'boolean') {
      return date.is_user_registered;
    }

    const registeredUsers = date?.participants ?? date?.users;
    if (!Array.isArray(registeredUsers) || !user?.id) {
      return false;
    }

    return registeredUsers.some((registeredUser) => {
      return Number(registeredUser.id) === Number(user.id);
    });
  }, [isMyDates, date?.is_user_registered, date?.participants, date?.users, user?.id]);

  const isDateArchived = Boolean(isArchivedParent || date?.workshop?.event?.archived_at);
  const isDateEnabled = Boolean(date?.is_enabled ?? true);
  const participantsCount = Number(date?.participants_count ?? 0);

  return (
    <>
      {onEditClick && !isDateArchived && (isAuthor || isLecturer || isAdmin) ? (
        <IconButton
          icon={<EditOutlined />}
          tooltip={t('Upraviť')}
          onClick={(e) => {
            e.stopPropagation();
            onEditClick(date);
          }}
        />
      ) : null}
      {onSignOnClick && !isUserRegistered && isFriOpenParticipant ? (
        <IconButton
          icon={<UserAddOutlined />}
          tooltip={t('Prihlásiť sa na tento termín')}
          onClick={(e) => {
            e.stopPropagation();
            onSignOnClick(date);
          }}
        />
      ) : null}
      {onUnsignOnClick && isUserRegistered && isFriOpenParticipant ? (
        <IconButton
          icon={<UserDeleteOutlined />}
          tooltip={t('Odhlásiť sa z tohto termínu')}
          onClick={(e) => {
            e.stopPropagation();
            onUnsignOnClick(date);
          }}
        />
      ) : null}
      {onDetailClick ? (
        <IconButton
          icon={<FileSearchOutlined />}
          tooltip={t('Zobraziť detail termínu')}
          onClick={(e) => {
            e.stopPropagation();
            onDetailClick(date);
          }}
        />
      ) : null}
      {onParticipantsClick && !isMyDates && (isAuthor || isLecturer || isAdmin) ? (
        <IconButton
          icon={<TeamOutlined />}
          tooltip={t('Účastníci')}
          onClick={(e) => {
            e.stopPropagation();
            onParticipantsClick(date);
          }}
          disabled={participantsCount === 0}
          disabledTooltip={t('Termín nemá prihlásených účastníkov')}
        />
      ) : null}
      {onToggleEnabled &&
      !isDateArchived &&
      (isLecturer || isAdmin) ? (
        <IconButton
          icon={isDateEnabled ? <StopOutlined /> : <CheckCircleOutlined />}
          tooltip={isDateEnabled ? t('Zrušiť termín') : t('Obnoviť termín')}
          onClick={(e) => {
            e.stopPropagation();
            onToggleEnabled(date);
          }}
        />
      ) : null}
      {onCopyClick &&
      !isDateArchived &&
      (isAuthor || isLecturer || isAdmin) &&
      !isMyDates ? (
        <IconButton
          icon={<CopyOutlined />}
          tooltip={t('Vytvoriť kópiu termínu')}
          onClick={(e) => {
            e.stopPropagation();
            onCopyClick(date);
          }}
        />
      ) : null}
      {onDelete &&
      !isDateArchived &&
      (isAuthor || isLecturer || isAdmin) &&
      !isMyDates ? (
        <IconButton
          icon={<DeleteOutlined />}
          tooltip={t('Vymazať')}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(date);
          }}
        />
      ) : null}
    </>
  );
}
